import { useState, useMemo, useEffect, useCallback } from 'react'
import { Alert, CircularProgress } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useDatabase } from '@/hooks/useDatabase'
import KPICards from '@/components/KPICards'
import { ExcelImport } from '@/components/ExcelImport'
import { MonthlyGrossChart, TopSuppliersChart } from '@/components/Charts'
import { InvoiceRow } from '@/types'
import {
  calculateKPIs,
  getInvoiceYears,
  getTopSuppliersByGross,
  grossByMonthOfYear,
} from '@/utils/filterUtils'
import { formatCurrency, formatCompactNumber } from '@/utils/formatters'
import { INVOICE_ENTITIES } from '@/utils/invoiceEntity'
import { useT } from '@/i18n/useT'
import {
  normalizeStatus,
  STATUS_LABELS,
  isOverdue,
  isDueSoon,
  daysUntilDue,
} from '@/utils/invoiceStatus'
import {
  Banner,
  Button,
  Card,
  CollapseBar,
  Dot,
  Icon,
  Page,
  PageHeader,
  Select,
  ToggleButton,
} from '@/components/ui'

const ALL = '__all__'

/** Parses the DD.MM.YYYY the API stores, falling back to ISO. */
function parseInvoiceDate(value: string | null): Date | null {
  if (!value) return null
  const dmy = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  const date = dmy
    ? new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
    : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { t } = useT()
  const [uploadedInvoices, setUploadedInvoices] = useState<InvoiceRow[] | null>(null)

  const { getInvoices } = useDatabase()

  const [dbInvoices, setDbInvoices] = useState<InvoiceRow[]>([])
  const [isUsingDb, setIsUsingDb] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showTaxKpis, setShowTaxKpis] = useState(false)
  // Collapsed by default, as in the design: the summary line on the bar already
  // carries Outstanding / Overdue / Gross, so the charts start above the fold.
  const [kpisOpen, setKpisOpen] = useState(false)
  const [year, setYear] = useState<string>(ALL)
  const [entity, setEntity] = useState<string>(ALL)

  const loadFromDatabase = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await getInvoices({ limit: 10000 })
      const mapped = result.data.map<InvoiceRow>((db) => ({
        supplierName: db.supplierName,
        dateInvoice: parseInvoiceDate(db.dateInvoice),
        brutto: db.brutto,
        netto: db.netto,
        vatAmount: db.vatAmount,
        taxFreeAmount: db.taxFreeAmount,
        taxableNetAmount: db.taxableNetAmount,
        rechnungsnummer: db.rechnungsnummer,
        comment: db.comment,
        status: db.status,
        dueDate: db.dueDate,
        documentType: db.documentType,
        entity: db.entity,
      }))
      setDbInvoices(mapped)
      setIsUsingDb(mapped.length > 0)
    } catch (err) {
      console.error('Failed to load from database:', err)
    } finally {
      setIsLoading(false)
    }
  }, [getInvoices])

  useEffect(() => {
    void loadFromDatabase()
  }, [loadFromDatabase])

  const allInvoices = isUsingDb ? dbInvoices : uploadedInvoices || []

  const handleDataLoaded = (data: InvoiceRow[]) => {
    setUploadedInvoices(data)
    setIsUsingDb(false)
  }

  const years = useMemo(() => getInvoiceYears(allInvoices), [allInvoices])

  // Year and entity narrow everything below: KPIs, both charts and the list
  const activeInvoices = useMemo(() => {
    let list = allInvoices
    if (year !== ALL) {
      const y = Number(year)
      list = list.filter((inv) => inv.dateInvoice?.getFullYear() === y)
    }
    if (entity !== ALL) list = list.filter((inv) => inv.entity === entity)
    return list
  }, [allInvoices, year, entity])

  const kpi = useMemo(() => calculateKPIs(activeInvoices), [activeInvoices])
  const chartYear = year === ALL ? (years[0] ?? new Date().getFullYear()) : Number(year)
  const monthly = useMemo(
    () => grossByMonthOfYear(activeInvoices, chartYear),
    [activeInvoices, chartYear],
  )
  const topSuppliers = useMemo(() => getTopSuppliersByGross(activeInvoices, 5), [activeInvoices])

  const currentMonthIndex =
    chartYear === new Date().getFullYear() ? new Date().getMonth() : undefined

  /** Invoices that still need the reviewer's attention, most urgent first. */
  const attention = useMemo(() => {
    const rank = (inv: InvoiceRow) => {
      if (isOverdue(inv.dueDate, inv.status, inv.documentType)) return 0
      if (normalizeStatus(inv.status) === 'in_review') return 1
      return 2
    }
    return activeInvoices
      .filter(
        (inv) =>
          isOverdue(inv.dueDate, inv.status, inv.documentType) ||
          isDueSoon(inv.dueDate, inv.status, inv.documentType) ||
          normalizeStatus(inv.status) === 'in_review',
      )
      .sort((a, b) => {
        const r = rank(a) - rank(b)
        if (r !== 0) return r
        return (daysUntilDue(a.dueDate) ?? 9999) - (daysUntilDue(b.dueDate) ?? 9999)
      })
  }, [activeInvoices])

  if (isLoading && !allInvoices.length) {
    return (
      <Page>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            padding: '64px 0',
          }}
        >
          <CircularProgress size={40} />
          <div style={{ fontSize: 15, color: 'var(--muted)' }}>{t('Loading data …')}</div>
        </div>
      </Page>
    )
  }

  if (!allInvoices.length) {
    return (
      <Page>
        <PageHeader
          title={t('Import data')}
          actions={
            <Button variant="outline" onClick={() => void loadFromDatabase()} disabled={isLoading}>
              <Icon name="refresh" size={15} strokeWidth={2.2} />
              {isLoading ? t('Loading …') : t('Refresh from database')}
            </Button>
          }
        />
        <Card>
          <ExcelImport onDataLoaded={handleDataLoaded} fileName={t('Invoices')} />
        </Card>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('Dashboard')}
        actions={
          <>
            <Select<string>
              value={year}
              onChange={setYear}
              ariaLabel={t('Year')}
              options={[
                { value: ALL, label: t('All years') },
                ...years.map((y) => ({ value: String(y), label: `${t('Year')} ${y}` })),
              ]}
            />
            <Select<string>
              value={entity}
              onChange={setEntity}
              ariaLabel={t('Legal entity')}
              options={[
                { value: ALL, label: t('All entities') },
                ...INVOICE_ENTITIES.map((e) => ({ value: e, label: e })),
              ]}
            />
            <ToggleButton active={showTaxKpis} onClick={() => setShowTaxKpis((v) => !v)}>
              {t('Tax details')}
            </ToggleButton>
            {uploadedInvoices && (
              <Button variant="ghost" onClick={() => setUploadedInvoices(null)}>
                {t('Different file')}
              </Button>
            )}
            <Button variant="outline" onClick={() => void loadFromDatabase()} disabled={isLoading}>
              <Icon name="refresh" size={15} strokeWidth={2.2} />
              {t('Refresh')}
            </Button>
          </>
        }
      />

      {uploadedInvoices && (
        <Alert severity="info">
          {t('{n} invoices from the uploaded file.').replace('{n}', String(uploadedInvoices.length))}
        </Alert>
      )}

      {(kpi.overdueCount > 0 || kpi.dueSoonCount > 0) && (
        <Banner tone={kpi.overdueCount > 0 ? 'warn' : 'ok'}>
          {kpi.overdueCount > 0 && (
            <strong style={{ color: 'var(--text)' }}>
              {kpi.overdueCount} {t('overdue')} ({formatCurrency(kpi.overdueAmount)})
            </strong>
          )}
          {kpi.overdueCount > 0 && kpi.dueSoonCount > 0 && ' · '}
          {kpi.dueSoonCount > 0 &&
            `${kpi.dueSoonCount} ${t('due within 7 days')} (${formatCurrency(kpi.dueSoonAmount)})`}
        </Banner>
      )}

      <CollapseBar
        label={t('Key figures')}
        summary={`${t('Outstanding')} ${formatCompactNumber(kpi.outstandingAmount)} € · ${t('Overdue')} ${formatCompactNumber(kpi.overdueAmount)} € · ${t('Gross')} ${formatCompactNumber(kpi.totalBrutto)} €`}
        open={kpisOpen}
        onToggle={() => setKpisOpen((v) => !v)}
      />
      {kpisOpen && <KPICards kpi={kpi} isLoading={isLoading} showTax={showTaxKpis} />}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <MonthlyGrossChart
          data={monthly}
          isLoading={isLoading}
          currentMonthIndex={currentMonthIndex}
        />
        <TopSuppliersChart data={topSuppliers} isLoading={isLoading} />
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>
            {t('Needs attention')} ({attention.length})
          </div>
          <button type="button" className="fx-link-quiet" onClick={() => navigate('/database')}>
            {t('View all in Database')} →
          </button>
        </div>

        {attention.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--muted)', padding: '16px 0' }}>
            {t('Nothing needs attention — no overdue, due-soon, or in-review invoices.')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 8 }}>
            {attention.slice(0, 8).map((inv, i) => {
              const s = normalizeStatus(inv.status)
              const overdue = isOverdue(inv.dueDate, inv.status, inv.documentType)
              const soon = !overdue && isDueSoon(inv.dueDate, inv.status, inv.documentType)
              const d = daysUntilDue(inv.dueDate)
              const dueLabel = inv.dueDate
                ? `${inv.dueDate}${
                    d != null
                      ? d < 0
                        ? ` · ${t('{n} days overdue').replace('{n}', String(-d))}`
                        : ` · ${t('in {n} days').replace('{n}', String(d))}`
                      : ''
                  }`
                : '—'
              return (
                <div
                  key={`${inv.rechnungsnummer}-${i}`}
                  className="fx-row-hoverable"
                  onClick={() => navigate('/database')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 4px',
                    borderBottom: '1px solid var(--line2)',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                  }}
                >
                  <Dot
                    color={overdue ? 'var(--err)' : soon ? 'var(--warn)' : 'var(--accent)'}
                    size={8}
                  />
                  <div style={{ fontSize: 13, flex: 1, minWidth: 160 }}>
                    <span style={{ fontWeight: 600 }}>{inv.supplierName}</span>
                    <span style={{ color: 'var(--muted)' }}>
                      {' · '}
                      {inv.rechnungsnummer || '—'}
                      {' · '}
                      {t(STATUS_LABELS[s])}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: overdue ? 'var(--err)' : soon ? 'var(--warn)' : 'var(--muted)',
                      fontWeight: overdue || soon ? 700 : 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dueLabel}
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, minWidth: 110, textAlign: 'right' }}
                  >
                    {formatCurrency(inv.brutto)}
                  </div>
                </div>
              )
            })}
            {attention.length > 8 && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
                +{attention.length - 8} {t('more — see Database')}
              </div>
            )}
          </div>
        )}
      </Card>
    </Page>
  )
}
